import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "../api.config";

export const flipperBudgetsApi = createApi({
  reducerPath: "flipperBudgetsApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["FlipperBudget", "FlipperBudgetStats"],
  endpoints: (builder) => ({
    // Get all budgets
    getAllBudgets: builder.query({
      query: ({ projectId } = {}) => {
        const params = new URLSearchParams();
        if (projectId) params.append("projectId", projectId);
        return `/flipper/budgets?${params.toString()}`;
      },
      providesTags: (result) =>
        result?.budgets
          ? [
              ...result.budgets.map(({ _id }) => ({ type: "FlipperBudget", id: _id })),
              { type: "FlipperBudget", id: "LIST" },
            ]
          : [{ type: "FlipperBudget", id: "LIST" }],
    }),

    // Get budget by ID
    getBudgetById: builder.query({
      query: (id) => `/flipper/budgets/${id}`,
      providesTags: (result, error, id) => [{ type: "FlipperBudget", id }],
    }),

    // Create budget
    createBudget: builder.mutation({
      query: (budgetData) => ({
        url: "/flipper/budgets",
        method: "POST",
        body: budgetData,
      }),
      invalidatesTags: [
        { type: "FlipperBudget", id: "LIST" },
        { type: "FlipperBudgetStats" },
      ],
    }),

    // Update budget
    updateBudget: builder.mutation({
      query: ({ id, ...budgetData }) => ({
        url: `/flipper/budgets/${id}`,
        method: "PUT",
        body: budgetData,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "FlipperBudget", id },
        { type: "FlipperBudget", id: "LIST" },
        { type: "FlipperBudgetStats" },
      ],
    }),

    // Delete budget
    deleteBudget: builder.mutation({
      query: (id) => ({
        url: `/flipper/budgets/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [
        { type: "FlipperBudget", id: "LIST" },
        { type: "FlipperBudgetStats" },
      ],
    }),

    // Add budget item
    addBudgetItem: builder.mutation({
      query: ({ budgetId, ...itemData }) => ({
        url: `/flipper/budgets/${budgetId}/items`,
        method: "POST",
        body: itemData,
      }),
      invalidatesTags: (result, error, { budgetId }) => [
        { type: "FlipperBudget", id: budgetId },
        { type: "FlipperBudget", id: "LIST" },
        { type: "FlipperBudgetStats" },
      ],
    }),

    // Update budget item
    updateBudgetItem: builder.mutation({
      query: ({ budgetId, itemId, ...itemData }) => ({
        url: `/flipper/budgets/${budgetId}/items/${itemId}`,
        method: "PUT",
        body: itemData,
      }),
      invalidatesTags: (result, error, { budgetId }) => [
        { type: "FlipperBudget", id: budgetId },
        { type: "FlipperBudget", id: "LIST" },
        { type: "FlipperBudgetStats" },
      ],
    }),

    // Delete budget item
    deleteBudgetItem: builder.mutation({
      query: ({ budgetId, itemId }) => ({
        url: `/flipper/budgets/${budgetId}/items/${itemId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { budgetId }) => [
        { type: "FlipperBudget", id: budgetId },
        { type: "FlipperBudget", id: "LIST" },
        { type: "FlipperBudgetStats" },
      ],
    }),

    // Toggle item paid status
    toggleItemPaid: builder.mutation({
      query: ({ budgetId, itemId }) => ({
        url: `/flipper/budgets/${budgetId}/items/${itemId}/toggle-paid`,
        method: "PATCH",
      }),
      invalidatesTags: (result, error, { budgetId }) => [
        { type: "FlipperBudget", id: budgetId },
        { type: "FlipperBudget", id: "LIST" },
        { type: "FlipperBudgetStats" },
      ],
    }),

    // Get stats
    getBudgetStats: builder.query({
      query: () => "/flipper/budgets/stats",
      providesTags: [{ type: "FlipperBudgetStats" }],
    }),
  }),
});

export const {
  useGetAllBudgetsQuery,
  useGetBudgetByIdQuery,
  useCreateBudgetMutation,
  useUpdateBudgetMutation,
  useDeleteBudgetMutation,
  useAddBudgetItemMutation,
  useUpdateBudgetItemMutation,
  useDeleteBudgetItemMutation,
  useToggleItemPaidMutation,
  useGetBudgetStatsQuery,
} = flipperBudgetsApi;
